import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';

import { caseDesignAttributes } from '../helpers/form';
import { Typography } from '../../../components/Wrappers/Wrappers';
import TagSelect from './TagSelect';

const TEETH_GROUPS = [
    {
        label: 'Adult upper teeth',
        teeth: ['11', '12', '13', '14', '15', '16', '17', '18', '21', '22', '23', '24', '25', '26', '27', '28'],
    },
    {
        label: 'Adult lower teeth',
        teeth: ['31', '32', '33', '34', '35', '36', '37', '38', '41', '42', '43', '44', '45', '46', '47', '48'],
    },
    {
        label: 'Deciduous upper teeth',
        teeth: ['51', '52', '53', '54', '55', '61', '62', '63', '64', '65'],
    },
    {
        label: 'Deciduous lower teeth',
        teeth: ['71', '72', '73', '74', '75', '81', '82', '83', '84', '85'],
    },
];

const useStyles = makeStyles((theme) => ({
    teethFormControl: {
        marginBottom: 35,
        width: '100%',
    },
    teethGroup: {
        marginBottom: theme.spacing(1.5),
    },
    teethGroupLabel: {
        fontSize: '0.8rem',
        color: 'rgba(0,0,0,0.54)',
        marginBottom: theme.spacing(0.75),
        display: 'block',
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.6, 0.6),
    },
    tag: {
        padding: theme.spacing(0.6, 1),
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        fontSize: '0.8125rem',
        fontWeight: 400,
        userSelect: 'none',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    tagSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f0f7ff',
    },
    tagError: {
        borderColor: theme.palette.error.main,
    },
    tableHeaderCell: {
        fontSize: '0.8rem',
        fontWeight: 700,
        padding: '3px 8px',
        lineHeight: 1.4,
    },
    tableBodyCell: {
        fontSize: '0.8rem',
        padding: '3px 8px',
        lineHeight: 1.4,
    },
}));

const FREE_REFINEMENT_PLANS = [
    { plan: 'Express 5', entitlement: 'Unlimited within 6 months' },
    { plan: 'Short 10', entitlement: 'Unlimited within 1 year' },
    { plan: 'Medium 20', entitlement: 'Unlimited within 2 years' },
    { plan: 'Long 30', entitlement: 'Unlimited within 3 years' },
    { plan: 'Comprehensive 30+', entitlement: 'Unlimited within 4 years' },
];

const TreatmentTypeNote = () => {
    const classes = useStyles();
    return (
        <Box
            style={{
                marginBottom: 35,
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                borderLeft: '3px solid #F16567',
            }}
        >
            <Typography weight="bold" style={{ marginBottom: 12 }}>
                Reminder of free refinements T&amp;Cs:
            </Typography>

            <Box component="ul" style={{ margin: '0 0 16px 0', paddingLeft: 20 }}>
                <Box component="li" style={{ marginBottom: 6 }}>
                    <Typography variant="body2">
                        Each free refinement must start within two months after the patient finishes wearing their last
                        step of aligners of the last treatment, whether from the initial plan or from a previous
                        refinement.
                    </Typography>
                </Box>
                <Box component="li">
                    <Typography variant="body2">
                        Free refinement case must not have any changes in target positions compared to the initial
                        treatment plan. Otherwise it will be deemed as a new case.
                    </Typography>
                </Box>
            </Box>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.tableHeaderCell}>Initial plan was</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Free refinement entitlement</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {FREE_REFINEMENT_PLANS.map(({ plan, entitlement }) => (
                        <TableRow key={plan}>
                            <TableCell className={classes.tableBodyCell}>{plan}</TableCell>
                            <TableCell className={classes.tableBodyCell}>{entitlement}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

const GroupedTeethSelect = ({ value = [], onChange, error }) => {
    const classes = useStyles();

    const handleToggle = (tooth) => {
        const isSelected = value.includes(tooth);
        const newValue = isSelected ? value.filter((t) => t !== tooth) : [...value, tooth];
        onChange({ target: { name: 'refinementProblemTeeth', value: newValue } });
    };

    return (
        <>
            {TEETH_GROUPS.map((group) => (
                <Box key={group.label} className={classes.teethGroup}>
                    <span className={classes.teethGroupLabel}>{group.label}</span>
                    <Box className={classes.tagsContainer}>
                        {group.teeth.map((tooth) => (
                            <Box
                                key={tooth}
                                className={`${classes.tag} ${value.includes(tooth) ? classes.tagSelected : ''} ${
                                    error ? classes.tagError : ''
                                }`}
                                onClick={() => handleToggle(tooth)}
                                role="button"
                                tabIndex={0}
                                aria-pressed={value.includes(tooth)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleToggle(tooth);
                                    }
                                }}
                            >
                                {tooth}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </>
    );
};

const buildStepOptions = (count) => {
    const n = parseInt(count, 10);
    if (!n || n <= 0) return [];
    return Array.from({ length: n }, (_, i) => String(i + 1));
};

const CaseDesignRefine = ({ refinementOrder, allowFullArchTreatment, formErrors, onTextFieldChange }) => {
    const classes = useStyles();

    const upperStepOptions = buildStepOptions(refinementOrder.upperStepsCount);
    const lowerStepOptions = buildStepOptions(refinementOrder.lowerStepsCount);

    const renderField = (attr) => {
        if (attr.hidden) return null;
        
        if (attr.id === 'mainComplaints' && attr.type === 'multiselect') {
            return (
                <TagSelect
                    key={attr.id}
                    id={attr.id}
                    label={attr.label}
                    options={attr.items}
                    value={refinementOrder[attr.id] || []}
                    onChange={onTextFieldChange}
                    required={attr.required}
                    error={formErrors[attr.id]}
                    helperText={attr.helperText}
                />
            );
        }

        return (
            <FormControl key={attr.id} variant="outlined" style={{ marginBottom: 35 }}>
                <InputLabel id={`${attr.id}-label`} required={attr.required}>
                    {attr.label}
                </InputLabel>
                <Select
                    labelId={`${attr.id}-label`}
                    id={attr.id}
                    value={refinementOrder[attr.id] || ''}
                    name={attr.id}
                    onChange={onTextFieldChange}
                    label={attr.label}
                    error={formErrors[attr.id]}
                >
                    {attr.items.map((item) => (
                        <MenuItem key={item.value || item} value={item.value || item}>
                            {item.displayValue || item}
                        </MenuItem>
                    ))}
                </Select>
                {attr.helperText && <FormHelperText>{attr.helperText}</FormHelperText>}
            </FormControl>
        );
    };

    // Get standard design attributes but exclude treatmentType (rendered separately as read-only)
    // and arches (rendered separately with conditional lock logic)
    const designFields = caseDesignAttributes(refinementOrder, allowFullArchTreatment).filter(
        (attr) => attr.id !== 'treatmentType' && attr.id !== 'arches'
    );

    const sourceArches = refinementOrder.sourceArches || 'Upper and lower';
    const isArchesLocked = sourceArches !== 'Upper and lower';

    const archesAttr = caseDesignAttributes(refinementOrder, allowFullArchTreatment).find(
        (attr) => attr.id === 'arches'
    );

    const renderArchesField = () => {
        if (!archesAttr) return null;
        if (!isArchesLocked) return renderField(archesAttr);
        return (
            <TextField
                key="arches"
                id="arches"
                label="Arches Selection"
                value={refinementOrder.arches || ''}
                variant="outlined"
                style={{ marginBottom: 35 }}
                required
                InputProps={{ readOnly: true }}
                helperText="Arches selection is inherited from the source order and cannot be changed"
            />
        );
    };

    const renderRetainerQuestion = () => {
        // React to the live arches value so the question appears when the user
        // changes arches from "Upper and lower" to a single-arch option too.
        const currentArches = refinementOrder.arches || '';
        if (currentArches === 'Upper only') {
            return (
                <FormControl key="includeLowerArchRetainer" variant="outlined" style={{ marginBottom: 35 }}>
                    <InputLabel id="includeLowerArchRetainer-label" required>
                        Lower Arch Retainer
                    </InputLabel>
                    <Select
                        labelId="includeLowerArchRetainer-label"
                        id="includeLowerArchRetainer"
                        value={refinementOrder.includeLowerArchRetainer || ''}
                        name="includeLowerArchRetainer"
                        onChange={onTextFieldChange}
                        label="Lower Arch Retainer"
                        error={formErrors.includeLowerArchRetainer}
                    >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </Select>
                    <FormHelperText error={formErrors.includeLowerArchRetainer}>
                        {formErrors.includeLowerArchRetainer
                            ? 'This field is required'
                            : 'Would you like to include a retainer for the lower arch?'}
                    </FormHelperText>
                </FormControl>
            );
        }
        if (currentArches === 'Lower only') {
            return (
                <FormControl key="includeUpperArchRetainer" variant="outlined" style={{ marginBottom: 35 }}>
                    <InputLabel id="includeUpperArchRetainer-label" required>
                        Upper Arch Retainer
                    </InputLabel>
                    <Select
                        labelId="includeUpperArchRetainer-label"
                        id="includeUpperArchRetainer"
                        value={refinementOrder.includeUpperArchRetainer || ''}
                        name="includeUpperArchRetainer"
                        onChange={onTextFieldChange}
                        label="Upper Arch Retainer"
                        error={formErrors.includeUpperArchRetainer}
                    >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </Select>
                    <FormHelperText error={formErrors.includeUpperArchRetainer}>
                        {formErrors.includeUpperArchRetainer
                            ? 'This field is required'
                            : 'Would you like to include a retainer for the upper arch?'}
                    </FormHelperText>
                </FormControl>
            );
        }
        return null;
    };

    return (
        <>
            <TreatmentTypeNote />

            {/* Treatment Type — read-only, pre-filled from source order */}
            <TextField
                id="treatmentType"
                label="Treatment Type"
                value={refinementOrder.treatmentType || ''}
                variant="outlined"
                style={{ marginBottom: 35 }}
                required
                InputProps={{ readOnly: true }}
                helperText="Treatment type is inherited from the source order and cannot be changed"
            />

            {/* mainComplaints comes first, before arches */}
            {designFields
                .slice(
                    0,
                    designFields.findIndex((a) => a.id === 'allowIPR')
                )
                .map(renderField)}

            {/* Arches — locked when source is Upper only / Lower only */}
            {renderArchesField()}
            {renderRetainerQuestion()}

            {/* Remaining design fields after arches */}
            {designFields.slice(designFields.findIndex((a) => a.id === 'allowIPR')).map(renderField)}

            <Divider style={{ marginBottom: 24 }} />

            <Typography variant="h6" weight="medium" style={{ marginBottom: 20 }}>
                Refinement Details
            </Typography>

            {/* Refinement Issue Description */}
            <TextField
                id="refinementIssueDescription"
                label="Refinement Issue Description"
                onChange={onTextFieldChange}
                value={refinementOrder.refinementIssueDescription || ''}
                name="refinementIssueDescription"
                multiline
                minRows="5"
                margin="normal"
                variant="outlined"
                style={{ marginBottom: 35 }}
                required
                error={
                    formErrors.refinementIssueDescription ||
                    (refinementOrder.refinementIssueDescription &&
                        refinementOrder.refinementIssueDescription.length > 800)
                }
                helperText={
                    refinementOrder.refinementIssueDescription &&
                    refinementOrder.refinementIssueDescription.length > 800
                        ? `Character limit exceeded (${refinementOrder.refinementIssueDescription.length}/800)`
                        : formErrors.refinementIssueDescription
                        ? 'This field is required'
                        : 'Describe the issue requiring a refinement (max 800 characters)'
                }
            />

            {/* Refinement Problem Teeth (optional) */}
            <FormControl className={classes.teethFormControl}>
                <FormLabel style={{ marginBottom: 12, color: 'rgba(0,0,0,0.87)', fontSize: '1rem', fontWeight: 500 }}>
                    Select the Teeth of Main Concern
                </FormLabel>
                <GroupedTeethSelect
                    value={refinementOrder.refinementProblemTeeth || []}
                    onChange={onTextFieldChange}
                    error={false}
                />
            </FormControl>

            {/* Last upper aligner — single-select dropdown, optional, only shown when steps exist */}
            {upperStepOptions.length > 0 && (
                <FormControl variant="outlined" style={{ marginBottom: 35 }}>
                    <InputLabel id="lastUpperAlignerWorn-label">Last upper aligner worn</InputLabel>
                    <Select
                        labelId="lastUpperAlignerWorn-label"
                        id="lastUpperAlignerWorn"
                        value={refinementOrder.lastUpperAlignerWorn || ''}
                        name="lastUpperAlignerWorn"
                        onChange={onTextFieldChange}
                        label="Last upper aligner worn"
                    >
                        {upperStepOptions.map((step) => (
                            <MenuItem key={step} value={step}>
                                Step {step}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Last upper aligner the patient was comfortably wearing</FormHelperText>
                </FormControl>
            )}

            {/* Last lower aligner — single-select dropdown, optional, only shown when steps exist */}
            {lowerStepOptions.length > 0 && (
                <FormControl variant="outlined" style={{ marginBottom: 35 }}>
                    <InputLabel id="lastLowerAlignerWorn-label">Last lower aligner worn</InputLabel>
                    <Select
                        labelId="lastLowerAlignerWorn-label"
                        id="lastLowerAlignerWorn"
                        value={refinementOrder.lastLowerAlignerWorn || ''}
                        name="lastLowerAlignerWorn"
                        onChange={onTextFieldChange}
                        label="Last lower aligner worn"
                    >
                        {lowerStepOptions.map((step) => (
                            <MenuItem key={step} value={step}>
                                Step {step}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Last lower aligner the patient was comfortably wearing</FormHelperText>
                </FormControl>
            )}
        </>
    );
};

export default CaseDesignRefine;
